import React, { createContext, useState, type ReactNode } from 'react';
import { Snackbar, Alert, Box, Typography, IconButton, Button, type AlertColor } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';

type ToastType = 'success' | 'error' | 'info' | 'warning' | 'processing';

interface Toast {
  id: string;
  message: string;
  description?: string;
  type: ToastType;
  open: boolean;
  persist?: boolean;
  actions?: React.ReactNode;
}

interface ToastContextData {
  showToast: (
    message: string,
    type?: ToastType,
    options?: {
      description?: string;
      persist?: boolean;
      actions?: React.ReactNode;
    }
  ) => void;
  closeToast: (id: string) => void;
}

export const ToastContext = createContext<ToastContextData>({} as ToastContextData);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast: ToastContextData['showToast'] = (
    message,
    type = 'info',
    options = {}
  ) => {
    const id = Date.now().toString();
    const newToast: Toast = {
      id,
      message,
      type,
      open: true,
      ...options,
    };
    setToasts((prev) => [...prev, newToast]);


    if (!options.persist && type !== 'processing') {
      setTimeout(() => {
        closeToast(id);
      }, type === 'error' ? 6000 : 4000);
    }
  };

  const closeToast = (id: string) => {
    setToasts((prev) =>
      prev.map((toast) =>
        toast.id === id ? { ...toast, open: false } : toast
      )
    );
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 300);
  };

  
  const getAlertProps = (toast: Toast) => {
    switch (toast.type) {
      case 'success':
        return {
          icon: <CheckCircleIcon sx={{ color: '#2e7d32' }} />,
          color: 'success' as AlertColor,
          borderLeft: '4px solid rgb(97, 203, 102)',
        };
      case 'error':
        return {
          icon: <ErrorOutlineIcon sx={{ color: '#c62828' }} />,
          color: 'error' as AlertColor,
          borderLeft: '4px solid rgb(219, 67, 67)',
        };
      case 'warning':
        return {
          icon: <WarningAmberOutlinedIcon sx={{ color: '#ed6c02' }} />,
          color: 'warning' as AlertColor,
          borderLeft: '4px solid #ed6c02',
        };
      case 'info':
        return {
          icon: <InfoOutlinedIcon sx={{ color: '#0288d1' }} />,
          color: 'info' as AlertColor,
          borderLeft: '4px solid #0288d1',
        };
      case 'processing':
        return {
          icon: <CircularProgress size={22} color="info" sx={{ mr: 1 }} />,
          color: 'info' as AlertColor,
          borderLeft: '4px solid #0288d1',
        };
      default:
        return {
          icon: <InfoOutlinedIcon />,
          color: 'info' as AlertColor,
          borderLeft: '4px solid #0288d1',
        };
    }
  };

  return (
    <ToastContext.Provider value={{ showToast, closeToast }}>
      {children}
      {toasts.map((toast, idx) => {
        const alertProps = getAlertProps(toast);
        const hasActions = !!toast.actions;
        return (
          <Snackbar
            key={toast.id}
            open={toast.open}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            style={{
              top: 24 + idx * 90,
              zIndex: 1400,
            }}
            onClose={() => closeToast(toast.id)}
            autoHideDuration={toast.persist || toast.type === 'processing' ? null : undefined}
          >
            <Alert
              icon={alertProps.icon}
              severity={alertProps.color}
              variant="filled"
              sx={{
                width: 370,
                alignItems: 'flex-start',
                boxShadow: 2,
                borderLeft: alertProps.borderLeft,
                pr: 1,
                pl: 1.5,
                py: 1.5,
                bgcolor: '#fff',
                color: '#222',
                fontSize: 15,
                fontWeight: 400,
                borderRadius: 1,
                minHeight: 60,
              }}
          
              action={
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={() => closeToast(toast.id)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              <Box>
                <Typography fontWeight={600} fontSize={16} mb={toast.description ? 0.5 : 0}>
                  {toast.message}
                </Typography>
                {toast.description && (
                  <Typography fontSize={14} color="text.secondary">
                    {toast.description}
                  </Typography>
                )}
              
                {hasActions && (
                  <Box
                    mt={2}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                   
                    <Box>{toast.actions}</Box>
                  
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => closeToast(toast.id)}
                    >
                      Ignorar
                    </Button>
                  </Box>
                )}
              </Box>
            </Alert>
          </Snackbar>
        );
      })}
    </ToastContext.Provider>
  );
}

